import {
	PartConfigurationDocumentValue,
	PartConfigurationPropertyEvaluation,
	PartConfigurationSchemaOption,
	PartConfigurationSchemaProperty,
	PartConfigurationSchemaValidationIssue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";

export function getPropertyModelDocumentValue(value: unknown): PartConfigurationDocumentValue | undefined {
	if (typeof value !== "object" || value == null) return undefined;
	if (!("value" in value) || typeof value.value !== "string") return undefined;
	if (!("fileName" in value) || typeof value.fileName !== "string") return undefined;
	if (!("documentDescription" in value) || typeof value.documentDescription !== "string") return undefined;
	if (!("documentCategoryId" in value) || typeof value.documentCategoryId !== "number") return undefined;
	return {
		value: value.value,
		fileName: value.fileName,
		documentDescription: value.documentDescription,
		documentCategoryId: value.documentCategoryId,
	};
}

/**
 * The options to choose from: those that the rules allow, and the current value even if they do not, so that the
 * field shows it with its issue. A free value that is not an option is shown as its own option.
 */
export function getPropertyModelSelectableOptions({
	property,
	evaluation,
}: {
	property: PartConfigurationSchemaProperty;
	evaluation: PartConfigurationPropertyEvaluation;
}): PartConfigurationSchemaOption[] {
	const allowedKeys = evaluation.allowedOptionKeys == null ? null : new Set(evaluation.allowedOptionKeys);
	const currentValue = typeof evaluation.value === "string" ? evaluation.value : null;
	const options = (property.options ?? []).filter(
		(option) => allowedKeys == null || allowedKeys.has(option.key) || option.key === currentValue,
	);
	if (currentValue != null && !options.some((option) => option.key === currentValue)) {
		options.push({ key: currentValue, label: currentValue, attributes: {} });
	}
	return options;
}

/** The range that the rules allow for a number. */
export function getPropertyModelFieldHelperText(evaluation: PartConfigurationPropertyEvaluation): string | undefined {
	if (evaluation.minValue != null && evaluation.maxValue != null)
		return `${evaluation.minValue}–${evaluation.maxValue}`;
	if (evaluation.minValue != null) return `≥ ${evaluation.minValue}`;
	if (evaluation.maxValue != null) return `≤ ${evaluation.maxValue}`;
	return undefined;
}

/** Issues about values that are still missing, such as on a new configuration. */
const MISSING_VALUE_ISSUE_TYPES = new Set(["REQUIRED_MISSING", "RULE_VALUE_PENDING"]);

/**
 * The issues to show in the form. A missing value, whether a required input or one that a rule reads, is shown only
 * after the user has edited the property or tried to save, so that a new configuration does not start full of errors.
 */
export function getShownPropertyModelIssues({
	issues,
	editedPropertyNames,
	showAllMissingValueIssues,
}: {
	issues: PartConfigurationSchemaValidationIssue[];
	editedPropertyNames: ReadonlySet<string>;
	showAllMissingValueIssues: boolean;
}): PartConfigurationSchemaValidationIssue[] {
	if (showAllMissingValueIssues) return issues;
	return issues.filter(
		(issue) =>
			!MISSING_VALUE_ISSUE_TYPES.has(issue.type) ||
			(issue.propertyName != null && editedPropertyNames.has(issue.propertyName)),
	);
}
